/* Get Insights — direct-action follow-up handlers. Wires "Add to slide" and
 * "Create new slide" to real Office.js code. "Make infographic" isn't handled
 * here — it routes to the Convert to Infographic panel instead (see
 * GetInsightsPanel's onMakeInfographic) so that panel can show its own
 * progress UI. */
import { useCallback, useState } from "react";
import { insertTextOnSlide } from "../../../../powerpoint";

const NEW_SLIDE_TITLE = "Insights";

const INSIGHTS_TEXTBOX_ADD_FAILED = "Couldn't add the insights to this slide. Try again.";
const INSIGHTS_NEW_SLIDE_FAILED = "Couldn't create the new slide. Try again.";

export type DirectFollowUpAction = "add_to_slide" | "create_new_slide";

export type DirectActionResult =
  | { status: "idle" }
  | { status: "busy"; action: DirectFollowUpAction }
  | { status: "done"; action: DirectFollowUpAction; message: string }
  | { status: "error"; action: DirectFollowUpAction; message: string };

function bulletText(insights: readonly string[]): string {
  return insights.map((insight) => `• ${insight}`).join("\n");
}

export function useInsightsFollowUp() {
  const [directResult, setDirectResult] = useState<DirectActionResult>({ status: "idle" });

  const run = useCallback(async (action: DirectFollowUpAction, insights: readonly string[]) => {
    setDirectResult({ status: "busy", action });
    const ok =
      action === "add_to_slide"
        ? await insertTextOnSlide(bulletText(insights), "current_textbox")
        : await insertTextOnSlide(`${NEW_SLIDE_TITLE}\n${bulletText(insights)}`, "new_slide");

    const message = action === "add_to_slide" ? "Insights added to the slide." : "New slide created with these insights.";
    const failure = action === "add_to_slide" ? INSIGHTS_TEXTBOX_ADD_FAILED : INSIGHTS_NEW_SLIDE_FAILED;
    setDirectResult(ok ? { status: "done", action, message } : { status: "error", action, message: failure });
  }, []);

  return { directResult, run };
}
